/**
 * Global format definitions, identifiers, and resolution helpers for formula execution
 * and formatting.
 *
 * Patterns are stored CANONICALLY (`.` decimal, `,` thousands) and rendered with the separators
 * configured in the tenant's settings.
 */

export type GlobalFormatKey =
  | 'currency'
  | 'integer'
  | 'decimal'
  | 'date'
  | 'dateWeekday'
  | 'dateTime'
  | 'dateTimeWeekday'
  | 'dayMonth'
  | 'percent'
  | 'monthYearLong'
  | 'monthYearShort'

export const DEFAULT_GLOBAL_FORMATS: Record<GlobalFormatKey, string> = {
  currency: '$#,##0.00; - $#,##0.00; -',
  integer: '#,##0; -#,##0; -',
  decimal: '#,##0.00; -#,##0.00; -',
  date: 'dd/mm/yyyy',
  dateWeekday: 'ddd dd/mm/yyyy',
  dateTime: 'dd/mm/yyyy hh:mm',
  dateTimeWeekday: 'ddd dd/mm/yyyy hh:mm',
  dayMonth: 'dd/mm',
  percent: '0.00%;',
  monthYearLong: 'mmmm yyyy',
  monthYearShort: 'mmm-yy',
}

const GLOBAL_FORMAT_ALIASES: Record<string, GlobalFormatKey> = {
  // currency
  currency: 'currency',
  moneda: 'currency',
  moeda: 'currency',

  // integer - the whole-number format quantity used to be, so the old names still
  // resolve: a formula that says `Quantity` was written against this pattern.
  integer: 'integer',
  entero: 'integer',
  inteiro: 'integer',
  quantity: 'integer',
  cantidad: 'integer',
  quantidade: 'integer',

  // decimal
  decimal: 'decimal',
  decimales: 'decimal',
  decimais: 'decimal',

  // date
  date: 'date',
  fecha: 'date',
  data: 'date',

  // dateWeekday
  dateweekday: 'dateWeekday',
  date_weekday: 'dateWeekday',
  date_with_weekday: 'dateWeekday',
  datewithweekday: 'dateWeekday',
  fecha_con_dia_de_semana: 'dateWeekday',
  fechacondiadesemana: 'dateWeekday',
  data_com_dia_da_semana: 'dateWeekday',
  datacomdiadasemana: 'dateWeekday',

  // dateTime
  datetime: 'dateTime',
  date_time: 'dateTime',
  date_and_time: 'dateTime',
  dateandtime: 'dateTime',
  fecha_y_hora: 'dateTime',
  fechayhora: 'dateTime',
  data_e_hora: 'dateTime',
  dataehora: 'dateTime',

  // dateTimeWeekday
  datetimeweekday: 'dateTimeWeekday',
  date_time_weekday: 'dateTimeWeekday',
  date_and_time_with_weekday: 'dateTimeWeekday',
  dateandtimewithweekday: 'dateTimeWeekday',
  fecha_y_hora_con_dia_de_semana: 'dateTimeWeekday',
  fechayhoracondiadesemana: 'dateTimeWeekday',
  data_e_hora_com_dia_da_semana: 'dateTimeWeekday',
  dataehoracomdiadasemana: 'dateTimeWeekday',

  // dayMonth
  daymonth: 'dayMonth',
  day_month: 'dayMonth',
  day_and_month: 'dayMonth',
  dayandmonth: 'dayMonth',
  dia_y_mes: 'dayMonth',
  diaymes: 'dayMonth',
  dia_e_mes: 'dayMonth',
  diaemes: 'dayMonth',

  // percent
  percent: 'percent',
  percentage: 'percent',
  porcentaje: 'percent',
  porcentagem: 'percent',

  // monthYearLong
  monthyearlong: 'monthYearLong',
  month_year_long: 'monthYearLong',
  month_and_year: 'monthYearLong',
  monthandyear: 'monthYearLong',
  mes_y_ano: 'monthYearLong',
  mes_y_año: 'monthYearLong',
  mesyano: 'monthYearLong',
  mesyaño: 'monthYearLong',
  mes_e_ano: 'monthYearLong',
  meseano: 'monthYearLong',

  // monthYearShort
  monthyearshort: 'monthYearShort',
  month_year_short: 'monthYearShort',
  short_month_and_year: 'monthYearShort',
  shortmonthandyear: 'monthYearShort',
  mes_y_ano_corto: 'monthYearShort',
  mes_y_año_corto: 'monthYearShort',
  mesyanocorto: 'monthYearShort',
  mesyañocorto: 'monthYearShort',
  mes_e_ano_curto: 'monthYearShort',
  meseanocurto: 'monthYearShort',
}

const normalizeAliasKey = (name: string): string =>
  name
    .trim()
    .normalize('NFC')
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/[áäà]/g, 'a')
    .replace(/[éëè]/g, 'e')
    .replace(/[íïì]/g, 'i')
    .replace(/[óöò]/g, 'o')
    .replace(/[úüù]/g, 'u')

/** Resolves an identifier or string to a canonical GlobalFormatKey, or undefined if not recognized. */
export function resolveGlobalFormatKey(name: string): GlobalFormatKey | undefined {
  const norm = normalizeAliasKey(name)
  const direct = GLOBAL_FORMAT_ALIASES[norm]
  if (direct !== undefined) return direct
  const spaceless = norm.replace(/_/g, '')
  return GLOBAL_FORMAT_ALIASES[spaceless]
}

/** Checks whether a given token/identifier name refers to a known global format. */
export function isGlobalFormatName(name: string): boolean {
  return resolveGlobalFormatKey(name) !== undefined
}

/**
 * Resolves a format string or global format name into an Excel pattern.
 * If the input corresponds to a global format name, returns its pattern from DEFAULT_GLOBAL_FORMATS
 * (or customFormats). Otherwise returns the input string unchanged as a custom pattern.
 */
export function resolveGlobalFormatPattern(
  formatOrName: string,
  customFormats?: Partial<Record<GlobalFormatKey, string>>,
): string {
  const key = resolveGlobalFormatKey(formatOrName)
  if (key !== undefined) {
    return customFormats?.[key] ?? DEFAULT_GLOBAL_FORMATS[key]
  }
  return formatOrName
}

export interface GlobalFormatCompletionOption {
  identifier: string
  key: GlobalFormatKey
  pattern: string
  label: string
}

export const GLOBAL_FORMAT_COMPLETIONS: readonly GlobalFormatCompletionOption[] = [
  { identifier: 'Date_with_weekday', key: 'dateWeekday', pattern: DEFAULT_GLOBAL_FORMATS.dateWeekday, label: 'Date_with_weekday' },
  { identifier: 'Date', key: 'date', pattern: DEFAULT_GLOBAL_FORMATS.date, label: 'Date' },
  { identifier: 'Date_and_time', key: 'dateTime', pattern: DEFAULT_GLOBAL_FORMATS.dateTime, label: 'Date_and_time' },
  { identifier: 'Date_and_time_with_weekday', key: 'dateTimeWeekday', pattern: DEFAULT_GLOBAL_FORMATS.dateTimeWeekday, label: 'Date_and_time_with_weekday' },
  { identifier: 'Day_and_month', key: 'dayMonth', pattern: DEFAULT_GLOBAL_FORMATS.dayMonth, label: 'Day_and_month' },
  { identifier: 'Month_and_year', key: 'monthYearLong', pattern: DEFAULT_GLOBAL_FORMATS.monthYearLong, label: 'Month_and_year' },
  { identifier: 'Short_month_and_year', key: 'monthYearShort', pattern: DEFAULT_GLOBAL_FORMATS.monthYearShort, label: 'Short_month_and_year' },
  { identifier: 'Currency', key: 'currency', pattern: DEFAULT_GLOBAL_FORMATS.currency, label: 'Currency' },
  { identifier: 'Integer', key: 'integer', pattern: DEFAULT_GLOBAL_FORMATS.integer, label: 'Integer' },
  { identifier: 'Decimal', key: 'decimal', pattern: DEFAULT_GLOBAL_FORMATS.decimal, label: 'Decimal' },
  { identifier: 'Percentage', key: 'percent', pattern: DEFAULT_GLOBAL_FORMATS.percent, label: 'Percentage' },
]

/**
 * The key a stored one means today.
 *
 * `quantity` was one format and is now two - `integer`, the whole-number pattern it
 * always was, and `decimal` beside it. Columns, summary cards and tenant settings
 * saved before the split still say `quantity`, so every place that reads a stored key
 * passes it through here rather than each inventing its own fallback.
 */
export function migrateGlobalFormatKey(key: string): string {
  return key === 'quantity' ? 'integer' : key
}
